/**
 * Arrivée imprévue — the shared decision vocabulary.
 *
 * Everything here is pure and dependency-free so the API, the web console,
 * and the mobile app agree on one set of rules rather than three
 * re-implementations. The two facts this module keeps apart, and which the
 * rest of the feature depends on keeping apart:
 *
 *   decision — what the RÉSIDENT tapped. Attributable.
 *   outcome  — what the GARDIEN did at the gate. His word.
 *
 * A reason code is attached to exactly the case where those two diverge in
 * the dangerous direction: someone was let in without an explicit approval.
 */
import { z } from "zod/v4";

export const SPOT_ARRIVAL_PURPOSES = [
  "livraison",
  "travaux",
  "visite",
  "autre",
] as const;
export type SpotArrivalPurpose = (typeof SPOT_ARRIVAL_PURPOSES)[number];
export const spotArrivalPurposeSchema = z.enum(SPOT_ARRIVAL_PURPOSES);

export const spotArrivalPurposeLabels: Record<SpotArrivalPurpose, string> = {
  livraison: "Livraison",
  travaux: "Travaux",
  visite: "Visite",
  autre: "Autre",
};

/**
 * Purposes for which an identity-document number may be collected at all.
 * Data minimization is enforced here, not in the UI: the API rejects an
 * `idNumber` sent with any other purpose.
 */
const ID_NUMBER_PURPOSES: readonly SpotArrivalPurpose[] = [
  "livraison",
  "travaux",
];

export function allowsIdNumber(purpose: SpotArrivalPurpose): boolean {
  return ID_NUMBER_PURPOSES.includes(purpose);
}

/**
 * Purposes for which « Laisser à la loge » / « Garder à la loge » exist at all.
 *
 * A parcel is the only thing that can be left behind. « Travaux » and
 * « visite » arrive as a person, and a person cannot be kept at the desk — so
 * offering the action there would produce a record that reads as custody of a
 * human being. Enforced server-side on both `respond` and `close`, not merely
 * hidden in the UI.
 */
export function allowsDeskOutcome(purpose: SpotArrivalPurpose): boolean {
  return purpose === "livraison";
}

/** How much of an identity-document number is ever kept. */
export const ID_SUFFIX_LENGTH = 3;

/**
 * The ONLY thing an arrivée imprévue keeps of a typed ID number: its last
 * three characters, normalised.
 *
 * The gardien still types the whole number — he is reading it off the card in
 * front of him, and asking him to type only the tail would get the wrong three
 * characters half the time. What crosses into storage is this suffix, so there
 * is no plaintext CIN at rest, nothing to purge on a timer, and nothing a
 * database leak could turn back into a national ID number.
 *
 * Three characters is enough to do the one job the number was collected for:
 * a syndic holding a claim can ask the contractor's employer for the CIN and
 * check the tail matches. It is far too little to identify anyone with, which
 * is the point — GATELY-47 replaced the 90-day plaintext window with this.
 *
 * Normalisation strips everything that is not a letter or a digit and
 * uppercases the rest, so the same card typed « ab-123-456 », « AB 123 456 »
 * and « AB123456 » all reduce to one suffix and read identically in the
 * syndic's log. Anything shorter than three characters is kept whole; input
 * that normalises to nothing is null — « no number was given ».
 *
 * DIGITS ARE FOLDED TO ASCII FIRST, and that is not a nicety. A CIN marocaine
 * prints its digits in Arabic-Indic, and a gardien on an Arabic keyboard types
 * them that way; without folding, « ١٢٣٤٥٦ » and « 123456 » would store two
 * different suffixes for one card, and a syndic checking « ٤٥٦ » against the
 * CIN an employer produced on a claim would conclude they disagree. NFKC
 * handles the compatibility forms (fullwidth, and the Arabic presentation
 * forms) but deliberately leaves Arabic-Indic and Eastern Arabic-Indic digits
 * as distinct characters, so those two blocks are mapped explicitly.
 */
const ASCII_DIGITS: Record<string, string> = {
  // U+0660–U+0669 Arabic-Indic ٠١٢٣٤٥٦٧٨٩
  ...Object.fromEntries(
    Array.from({ length: 10 }, (_, i) => [
      String.fromCharCode(0x0660 + i),
      String(i),
    ]),
  ),
  // U+06F0–U+06F9 Eastern Arabic-Indic (Persian/Urdu) ۰۱۲۳۴۵۶۷۸۹
  ...Object.fromEntries(
    Array.from({ length: 10 }, (_, i) => [
      String.fromCharCode(0x06f0 + i),
      String(i),
    ]),
  ),
};

export function idNumberSuffix(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const folded = raw
    .normalize("NFKC")
    .replace(/[\u0660-\u0669\u06f0-\u06f9]/g, (d) => ASCII_DIGITS[d] ?? d);
  const normalised = folded.toUpperCase().replace(/[^0-9A-Z]/g, "");
  if (normalised.length === 0) return null;
  return normalised.slice(-ID_SUFFIX_LENGTH);
}

/**
 * Every value `spot_arrival.status` can hold, mirroring the pgEnum. Declared
 * here so a switch over it can be exhaustive on all three surfaces — the api
 * package used to keep its own copy of this union, which is one list too many
 * for a value the database already constrains.
 */
export const SPOT_ARRIVAL_STATUSES = [
  "pending",
  "answered",
  "no_answer",
  "closed",
  "expired",
  "cancelled",
] as const;
export type SpotArrivalStatus = (typeof SPOT_ARRIVAL_STATUSES)[number];
export const spotArrivalStatusSchema = z.enum(SPOT_ARRIVAL_STATUSES);

export const SPOT_ARRIVAL_REASON_CODES = [
  "phone_confirmed",
  "emergency",
  "syndic_authorized",
  "known_visitor",
  "no_contact",
  "admitted_despite_refusal",
] as const;
export type SpotArrivalReasonCode = (typeof SPOT_ARRIVAL_REASON_CODES)[number];
export const spotArrivalReasonSchema = z.enum(SPOT_ARRIVAL_REASON_CODES);

export const spotArrivalReasonLabels: Record<SpotArrivalReasonCode, string> = {
  phone_confirmed: "Confirmé par téléphone",
  emergency: "Urgence / secours",
  syndic_authorized: "Autorisé par le syndic",
  known_visitor: "Visiteur habituel reconnu",
  no_contact: "Admis sans contact",
  admitted_despite_refusal: "Admis malgré un refus",
};

export type SpotArrivalPolicy = "deny" | "discretion";
export const spotArrivalPolicySchema = z.enum(["deny", "discretion"]);

/**
 * What the RÉSIDENT tapped.
 *
 * `leave_at_desk` is a third ANSWER, not a variant of approval: the résident
 * neither let the courier in nor turned them away — they asked for the parcel
 * to stay. Offered, and accepted server-side, only for `livraison`.
 */
export type SpotArrivalDecision = "approved" | "rejected" | "leave_at_desk";

/**
 * What the GARDIEN recorded at the gate.
 *
 * `kept_at_desk` writes NO entry_event: the courier did not enter and was not
 * turned away. It is the one outcome that creates a row in another table
 * (`parcel`) instead, in the same transaction.
 */
export type SpotArrivalOutcome = "admitted" | "turned_away" | "kept_at_desk";
export type ReasonSeverity = "routine" | "exceptional";

/**
 * How loudly a code should read in the syndic's log.
 *
 * `no_contact` is the ONLY policy-sensitive code, and that is the entire
 * substance of the deny/discretion setting: under `deny`, admitting with no
 * résident contact at all is a violation; under `discretion` it is the
 * gardien exercising judgement the syndic delegated to him.
 *
 * `phone_confirmed` is deliberately routine. Phoning the résident is the
 * status quo this feature is replacing, so flagging it would mean flagging
 * nearly every arrival in week one and training the syndic to ignore the
 * flag — costing exactly the signal the flag exists to carry.
 */
export function resolveSeverity(
  code: SpotArrivalReasonCode,
  policy: SpotArrivalPolicy,
): ReasonSeverity {
  switch (code) {
    case "phone_confirmed":
    case "emergency":
    case "syndic_authorized":
      return "routine";
    case "known_visitor":
    case "admitted_despite_refusal":
      return "exceptional";
    case "no_contact":
      return policy === "deny" ? "exceptional" : "routine";
  }
}

/**
 * A reason is required if and only if someone was ADMITTED without an
 * explicit résident approval. Turning someone away never needs one — it is
 * the safe direction, and demanding paperwork for it would push gardiens
 * toward admitting instead. Keeping a parcel never needs one either, for the
 * same reason inverted: nobody entered, so there is nothing to justify, and
 * asking would push the gardien toward « Refuser » — the wrong record.
 *
 * `leave_at_desk` is NOT an approval. Admitting after it is an override of a
 * stated preference, so it does carry the motif question.
 */
export function isReasonRequired(a: {
  outcome: SpotArrivalOutcome;
  decision: SpotArrivalDecision | null;
}): boolean {
  if (a.outcome !== "admitted") return false;
  return a.decision !== "approved";
}

/**
 * Which codes may be attached, given what the résident said.
 *
 * Admitting against an explicit refusal is a categorically different act
 * from admitting into silence, so it gets its own code and that code is not
 * offered in any other situation.
 *
 * After `leave_at_desk` the résident did not refuse — so the refusal code is
 * not on offer — but they did state a preference, so the codes are narrowed to
 * the two that describe the RÉSIDENT (or the syndic on their behalf) changing
 * that preference: a later phone call, or the syndic's standing authorisation.
 * `no_contact` and `emergency` are excluded because somebody DID answer.
 *
 * `known_visitor` is deliberately excluded here, even though it is a valid
 * override code everywhere else: recognising the courier is the GARDIEN'S
 * judgement, not the résident changing their mind, and admitting on that
 * basis after an explicit « Laisser à la loge » is exactly the override
 * `resolveSeverity` grades `exceptional` for — narrowing the codes offered
 * here to the two that stay `routine` is what keeps that flag meaningful
 * (2026-09-02 execution ruling).
 */
const LEAVE_AT_DESK_OVERRIDE_CODES: readonly SpotArrivalReasonCode[] = [
  "phone_confirmed",
  "syndic_authorized",
];

export function validCodesFor(
  decision: SpotArrivalDecision | null,
): readonly SpotArrivalReasonCode[] {
  if (decision === "approved") return [];
  if (decision === "rejected") return ["admitted_despite_refusal"];
  if (decision === "leave_at_desk") return LEAVE_AT_DESK_OVERRIDE_CODES;
  return SPOT_ARRIVAL_REASON_CODES.filter(
    (c) => c !== "admitted_despite_refusal",
  );
}

/**
 * Provenance, DERIVED — never chosen by the gardien.
 *
 * `resident` only when the gate outcome matches what the résident actually
 * tapped. Everything else, including every case where nobody answered, is
 * the gardien's word and is recorded as such.
 *
 * The desk pair follows the same rule: a parcel kept because the résident
 * asked is attributable to them; a parcel kept after silence or a refusal is
 * the gardien's own call, and so is a courier admitted despite
 * « Laisser à la loge ».
 */
export function deriveOutcomeSource(a: {
  outcome: SpotArrivalOutcome;
  decision: SpotArrivalDecision | null;
}): "resident" | "gardien" {
  const matches =
    (a.outcome === "admitted" && a.decision === "approved") ||
    (a.outcome === "turned_away" && a.decision === "rejected") ||
    (a.outcome === "kept_at_desk" && a.decision === "leave_at_desk");
  return matches ? "resident" : "gardien";
}

/**
 * Whose word one arrival rests on, for a list that includes rows the gardien
 * never closed.
 *
 * `outcomeSource` is the stamped, derived-at-close answer and is authoritative
 * whenever it exists — never recompute it, because the policy and the facts it
 * was derived from are the ones that held at close time. But the states the
 * syndic's review page exists to surface — refused, expired, cancelled — never
 * reach close, so `outcomeSource` is NULL on exactly the rows he came to look
 * at. There, a résident tap is the only attributable act available, and its
 * absence means the record rests on the gardien alone.
 */
export function spotArrivalProvenance(a: {
  decision: SpotArrivalDecision | null;
  outcome: SpotArrivalOutcome | null;
  outcomeSource: "resident" | "gardien" | null;
}): "resident" | "gardien" {
  if (a.outcomeSource) return a.outcomeSource;
  return a.decision !== null ? "resident" : "gardien";
}

/**
 * Who to push at each rung of the escalation ladder.
 *
 * Stage 0 is the owner alone. Stage 1 is everyone else in the household —
 * the owner is excluded because he already got one 20 seconds ago, and a
 * second identical push reads as noise rather than urgency.
 *
 * Lives in this shared package, not in the api package, because the worker
 * runs stage 1 and cannot import `@gately/api`. One copy, one test.
 */
export function resolveRecipients(a: {
  ownerId: string | null;
  memberUserIds: string[];
  stage: 0 | 1;
}): string[] {
  if (a.stage === 0) return a.ownerId ? [a.ownerId] : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of a.memberUserIds) {
    if (id === a.ownerId) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * Who to push at each rung when the arrival may name the person visited
 * (borne spec §6.2).
 *
 * With a host: stage 0 is the host alone, stage 1 is the unit's ACTING
 * Responsable (`ownerId` here is always the acting resident — the tenant
 * during a lease), minus the host. Never the rest of the company: a picked
 * host means the visitor said who they came for.
 *
 * Without a host: exactly today's `resolveRecipients`.
 *
 * The API runs stage 0 in `spotArrival.create` and the worker runs stage 1
 * in its escalation; both call THIS function. Changing only one side would
 * page the host at stage 0 and then the whole company at stage 1.
 */
export function resolveSpotArrivalRecipients(a: {
  ownerId: string | null;
  memberUserIds: readonly string[];
  hostUserId: string | null;
  stage: 0 | 1;
}): string[] {
  if (a.hostUserId === null) {
    return resolveRecipients({
      ownerId: a.ownerId,
      memberUserIds: [...a.memberUserIds],
      stage: a.stage,
    });
  }
  if (a.stage === 0) return [a.hostUserId];
  return a.ownerId !== null && a.ownerId !== a.hostUserId ? [a.ownerId] : [];
}

/**
 * Building setting `organization.walk_in_id_policy` (borne spec §4.1). Applies
 * to every walk-in — desk and borne — and never to invited visitors, whose
 * invitation's form decides.
 */
export const WALK_IN_ID_POLICIES = ["off", "required"] as const;
export type WalkInIdPolicy = (typeof WALK_IN_ID_POLICIES)[number];
export const walkInIdPolicySchema = z.enum(WALK_IN_ID_POLICIES);

export const walkInIdPolicyLabels: Record<WalkInIdPolicy, string> = {
  off: "Facultative",
  required: "Obligatoire",
};

/** `organization.setWalkInIdPolicy` input. */
export const setWalkInIdPolicyInput = z.object({
  walkInIdPolicy: walkInIdPolicySchema,
});
export type SetWalkInIdPolicyInput = z.infer<typeof setWalkInIdPolicyInput>;

export type IdNumberRule = "required" | "optional" | "forbidden";

/**
 * Whether a walk-in's ID number must, may or must not be collected.
 *
 * `required` asks for it on EVERY motif: otherwise visitors would pick the
 * motif that skips it. Under `off`, data minimisation stands as before — a
 * personal visit has no reason to produce one (`allowsIdNumber`).
 */
export function idNumberRule(
  policy: WalkInIdPolicy,
  purpose: SpotArrivalPurpose,
): IdNumberRule {
  if (policy === "required") return "required";
  return allowsIdNumber(purpose) ? "optional" : "forbidden";
}

/** Host search: never a browse list (borne spec §4 step 2). */
export const HOST_SEARCH_MIN_LENGTH = 2;
export const HOST_SEARCH_MAX_RESULTS = 5;

/**
 * « Sara B. » — first name plus the initial of the next word. What a lobby or
 * list surface shows of the person visited: enough to recognise, too little
 * to look anyone up with. A single word is returned whole.
 */
export function hostDisplayName(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter((p) => p.length > 0);
  const first = parts[0] ?? "";
  const next = parts[1];
  if (next === undefined) return first;
  const initial = Array.from(next)[0] ?? "";
  return `${first} ${initial.toLocaleUpperCase("fr")}.`;
}

/**
 * « pour Sara B. · Glovo » when a host is set, the company alone otherwise
 * (borne spec §6.3). `/poste` cards, the syndic list and the visitor sheet all
 * render through this, so the three cannot drift.
 */
export function walkInDestinationLabel(a: {
  unitLabel: string;
  hostName: string | null;
}): string {
  const host = a.hostName ? hostDisplayName(a.hostName) : "";
  return host ? `pour ${host} · ${a.unitLabel}` : a.unitLabel;
}

/**
 * Escalation-ladder timings. Canonical here because every surface that
 * RENDERS the ladder (web station, mobile gardien queue) needs them and can
 * only depend on validators; the queue package re-exports them for the
 * worker/server side. Values are product decisions (spec decision #9 —
 * fixed, not configurable).
 */
export const SPOT_ARRIVAL_ESCALATE_DELAY_MS = 20_000;
/** After escalate: t+45s total → status `no_answer`. */
export const SPOT_ARRIVAL_TIMEOUT_DELAY_MS = 25_000;
/** t+10min → status `expired`; the request stops being actionable. */
export const SPOT_ARRIVAL_EXPIRE_DELAY_MS = 10 * 60_000;
